import { createContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { getSupabaseAuthClient, type SupabaseAuthOnlyClient } from '../lib/supabaseAuth';
import { createAuthStore, type AuthState, type AuthStore } from './authStore';
import { browserRecoveryMarker } from './recoveryMarker';

/**
 * React binding over the framework-agnostic auth store.
 *
 * The store is created with useMemo (construction is pure — no side effects), its
 * side effects are started in an effect, and it is disposed in that effect's
 * cleanup. State is surfaced with useSyncExternalStore, so this provider stays a
 * thin binding and the real logic lives in (and is tested through) the store.
 *
 * `client` is injectable so tests can supply an auth-only mock; in the app it
 * defaults to the shared browser auth client.
 */
export interface AuthContextValue extends AuthState {
  signInWithPassword: AuthStore['signInWithPassword'];
  signUp: AuthStore['signUp'];
  signOut: AuthStore['signOut'];
  resetPasswordForEmail: AuthStore['resetPasswordForEmail'];
  updateUser: AuthStore['updateUser'];
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Auth-only client; defaults to the shared browser auth client. */
  client?: SupabaseAuthOnlyClient;
}

export function AuthProvider({ children, client }: AuthProviderProps) {
  const store = useMemo(
    // The marker is constructed here rather than at module scope so no storage
    // is touched on import; it makes the recovery latch survive a reload.
    () => createAuthStore(client ?? getSupabaseAuthClient(), browserRecoveryMarker()),
    [client],
  );

  useEffect(() => {
    store.start();
    return () => {
      store.dispose();
    };
  }, [store]);

  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);

  const value: AuthContextValue = {
    ...state,
    signInWithPassword: store.signInWithPassword,
    signUp: store.signUp,
    signOut: store.signOut,
    resetPasswordForEmail: store.resetPasswordForEmail,
    updateUser: store.updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
